import { calendarConfig } from '../Constants';
import { DatesType, GetMonthParamsType, UnavailableDatesType } from '../Types';
import { getDaysArray, splitArray } from 'Components/Functions';
import { BankHolidaysAPI } from 'API/CalendarAPI';

const getCurrentMonthDow = (startDate: number): number => {
    if (calendarConfig.includeWeekends) {
        return  startDate == 0 ? 6 : startDate - 1;
    }
    else {
        if (startDate == 6 || startDate == 0) {
            return 0;
        }
        else {
            return startDate - 1;
        }
    }
}

const removeWeekends = (days: DatesType[]): DatesType[] => {
    const daysToRemove: number[] = [];
    days.forEach(removeDay => {
        const date = new Date(removeDay.date)
        if (date.getDay() == 6 || date.getDay() == 0) {
            const removeIdx = days.findIndex(day => day.date == removeDay.date);
            daysToRemove.push(removeIdx)
        }
    })
    return days.filter((v, i) => {
        return !daysToRemove.includes(i);
    });
}

const lockBankHolidays = (days: DatesType[]) => {
    days.filter((day: DatesType) => day.bankHoliday).forEach((bh: DatesType) => {
        const day = days.findIndex((day: DatesType) => day.date === bh.date);
        if (day > -1) {
            const date = new Date(days[day].date);
            const dayOfWeek = date.getDay();
            if (dayOfWeek === 1 || dayOfWeek === 5) {
                const offset = dayOfWeek === 1 ? 1 : -1;
                const adjacentIndex = day + offset;
                if (adjacentIndex >= 0 && adjacentIndex < days.length) {
                    days[adjacentIndex].selectable = true;
                }
            }
        }
    })
}

const getMonthDays = async (params: GetMonthParamsType) => {
    const months: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    for (let i = params.startDay; i <= params.endDay; i++) {
        const date = new Date(params.year, params.month, i);
        const monthStr = ("0" + (date.getMonth() + 1).toString()).slice(-2);
        const dayStr = ("0" + date.getDate().toString()).slice(-2);
        const yearStr = date.getFullYear().toString();
        const dateStr = [yearStr, monthStr, dayStr].join('-');

        const bankHolidaysForMonth = params.bankHolidays.filter(bh => bh.includes(`${yearStr}-${monthStr}`));
        const dateUnavailable = params.unavailableDates.filter((booking) => booking.booking_date.split("T")[0] == dateStr);

        let prevDateUnavailable:any = [];
        let nextDateUnavailable:any = [];

        if (dateUnavailable.length > 0) {
            const unavailableCurrentIdx = params.unavailableDates.findIndex((b) => b.booking_date == dateUnavailable[0].booking_date);
            if (unavailableCurrentIdx > 0) {
                const prevDate = params.unavailableDates[unavailableCurrentIdx - 1].booking_date;
                prevDateUnavailable = params.unavailableDates.filter((b) => b.booking_date == prevDate);
            }
            if (unavailableCurrentIdx + 1 < params.unavailableDates.length) {
                const nextDate = params.unavailableDates[unavailableCurrentIdx + 1].booking_date;
                nextDateUnavailable = params.unavailableDates.filter((b) => b.booking_date == nextDate);
            }
        }

        const obj = {
            dateString: dayStr + " " + months[params.month],
            date: dateStr,
            visible: params.visible,
            selectable: date > new Date() && ((date.getDay()) == 1 || (date.getDay()) == 5),
            selected: false,
            price: params.dailyPrices[i - 1],
            available: date > new Date() && dateUnavailable.length < 2,
            bankHoliday: bankHolidaysForMonth.includes(dateStr),
            isCollection: false,
            isReturn: false,
            bookedCollection: date > new Date() && dateUnavailable.length == 1 && nextDateUnavailable.length == 2,
            bookedReturn: date > new Date() && dateUnavailable.length == 1 && prevDateUnavailable.length == 2
        }
        params.days.push(obj);
    }
}

export const getDaysForCalendar = async function (month: number, year: number, dailyPrices: number[], unavailableDates: UnavailableDatesType[]) {
    const bankHolidays: string[] = await BankHolidaysAPI.get();
    let days: DatesType[] = [];
    const currentMonthDow = getCurrentMonthDow(new Date(year, month, 1).getDay());

    //using getDate with day of 0 returns previous month last day
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lastMonthNoOfDays = new Date(year, month, 0).getDate();
    const lastMonthYear = month == 0 ? year - 1 : year;
    const lastMonth = month == 0 ? 11 : month -1;

    //gets days for last month
    let params = {
        days,
        startDay: lastMonthNoOfDays - currentMonthDow + 1,
        endDay: lastMonthNoOfDays,
        year: lastMonthYear,
        month: lastMonth,
        visible: false,
        unavailableDates,
        dailyPrices,
        bankHolidays
    };
    await getMonthDays(params);

    params = {
        ...params,
        startDay: 1,
        endDay: daysInMonth,
        year: year,
        month: month,
        visible: true,
    };
    //gets days for last month
    await getMonthDays(params);

    //If bank holidays should not be selectable = if monday make tuesday selectable , if friday make thursday selectable 
    if (calendarConfig.lockBankHolidays) {
        lockBankHolidays(days);
    }

    //If weekends should not be included = remove them from array
    if (calendarConfig.includeWeekends == false) {
        days = removeWeekends(days);
    }

    return days;
};

export const getAllDays = (days: DatesType[][]) => {
    const daysArr:DatesType[] = [];
    days.forEach((dayRow: DatesType[]) => {
        dayRow.forEach((day: DatesType) => {
            daysArr.push(day);
        })
    });
    return daysArr;
}

export const highlightSelectedDates = (startDate: string, endDate: string, calendarDays: DatesType[][]) => {
    const daysPerRow = calendarConfig.includeWeekends ? 7 : 5;
    const daysArr = getAllDays(calendarDays);
    const dates = orderedDates(startDate, endDate);
    const daysSelection = getDaysArray(dates[0], dates[1]);
    daysArr.forEach((day: DatesType) => {
        day.selected = day.available && !day.bankHoliday && daysSelection.includes(day.date); 
        day.isCollection = day.date == daysSelection[0];
        day.isReturn = day.date == daysSelection[daysSelection.length - 1]; 
    })
    const daysSplit = splitArray(daysArr, daysPerRow);
    return daysSplit;
}

export const orderedDates = (startDate:string, endDate: string) => {
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    if (startDateObj > endDateObj) {
        startDate = endDateObj.toISOString().split('T')[0];
        endDate = startDateObj.toISOString().split('T')[0];
    }
    return [startDate, endDate];
}